import { ref, watch, onMounted, h, computed } from 'vue';
import { useAssistantStore } from '@/stores/assistantStore';
import MessageSources from '@/components/LegalAssistant/message-sources/MessageSources.vue';
import legalAssistantService from '@/services/LegalAssistantService';
import { AssistantType } from '@/models/assistant-types';
import { ChatExposedMethods } from '@/models/chat.models';
import { Conversation } from '@/models/conversation';
import { QuestionResponseDTO, SourceDTO } from '@/dto/QuestionResponseDTO';
import { ServerSentEvent } from '@/services/HttpClient/ResponseParser';
import { getSectionPriority, Section } from '@/core/sectionPriority';
import { useUserSettingsStore } from '@/stores/userSettingsStore';
import { AskQuestionSectionsStreamResponseChunk } from '@/dto/AskQuestionSectionsStreamResponseDTO';
import { AskQuestionSectionsStreamRequestDTO } from '@/dto/AskQuestionSectionsStreamRequestDTO';
import { CountryCodes } from '../models/countries';
import { v4 as uuidv4 } from 'uuid';
import {
  LegalAssistantMessage,
  LegalAssistantSectionsMessage
} from '@/models/messages';
import {
  isValidAnswer,
  INVALID_ANSWERS
} from '@/utils/answer-validation.utils';
import { santizeReferences } from '@/utils/utils';
import { useStreaming } from '@/composables/useStreaming';

export function useLegalAssistant() {
  const sectionChunks = ref<Record<string, string>>({});
  const sectionSources = ref<Record<string, SourceDTO[]>>({});
  const currentSectionIndex = ref(0);
  const activeTabIndex = ref(0);
  const legalAssistantRef = ref<ChatExposedMethods | null>(null);

  const userSettingsStore = useUserSettingsStore();
  const store = useAssistantStore();

  const currentProductIndex = userSettingsStore.selectedRagIndex;
  const SECTION_PRIORITY = getSectionPriority(currentProductIndex);
  const FIRST_SECTION = 0;
  const getCurrentSection = () => SECTION_PRIORITY[currentSectionIndex.value];

  const resetReactiveState = (): void => {
    sectionChunks.value = {};
    currentSectionIndex.value = FIRST_SECTION;
  };

  const nextSection = () => {
    currentSectionIndex.value++;
  };

  const setHeaderData = (
    assistant: AssistantType,
    data: {
      loading?: boolean;
      showNewConversation?: boolean;
      showHistory?: boolean;
    }
  ): void => {
    const storeActions = store.createAssistantActions();

    storeActions.setHeaderData({
      ...store.legalAssistantHeaderData,
      ...data
    });
  };

  const extractSection = (answerSection: string, error?: string): Section => {
    if (answerSection === '' && error) {
      const errorMatch = error.match(/\[(.+?)\]/);
      if (
        errorMatch &&
        Object.values(Section).includes(errorMatch[1] as Section)
      ) {
        return errorMatch[1] as Section;
      }
    }
    if (Object.values(Section).includes(answerSection as Section)) {
      return answerSection as Section;
    }
    return Section.Default;
  };

  const onChunk = (
    obj: ServerSentEvent<AskQuestionSectionsStreamResponseChunk>,
    conversation: Conversation
  ) => {
    const data = obj.data;

    if (data.conversationId) {
      conversation.id = data.conversationId;
    }

    const extractedSection = extractSection(
      data.answerSection,
      data.error ?? undefined
    );
    legalAssistantRef.value?.updateSectionResponse(
      santizeReferences(data.answer),
      extractedSection
    );
    sectionChunks.value = {
      ...sectionChunks.value,
      [extractedSection]: santizeReferences(data.answer)
    };
    if (data.sources && data.sources.length > 0) {
      sectionSources.value = {
        ...sectionSources.value,
        [extractedSection]: data.sources
      };
      legalAssistantRef.value?.updateSectionSources(
        data.sources,
        extractedSection
      );
    }
  };

  const askQuestion = async (
    text: string,
    conversation: Conversation,
    assistant: AssistantType
  ): Promise<QuestionResponseDTO> => {
    const response = await legalAssistantService.askQuestion(
      text,
      conversation,
      assistant === AssistantType.LegalAssistant
        ? conversation.ragIndex
        : undefined
    );
    setHeaderData(assistant, {
      loading: false,
      showNewConversation: true,
      showHistory: true
    });
    return response;
  };

  const buildChatHistory = (
    messages: LegalAssistantSectionsMessage[]
  ): Array<{
    question: string;
    answers: Array<{
      section: string;
      answer: string;
    }>;
  }> => {
    const chatHistory: Array<{
      question: string;
      answers: Array<{
        section: string;
        answer: string;
      }>;
    }> = [];

    for (let i = 0; i < messages.length - 1; i++) {
      const userMsg = messages[i];
      if (!userMsg.isBot) {
        const botMsg = messages[i + 1];
        if (botMsg && botMsg.isBot && botMsg.sectionResponses) {
          const answers: Array<{ section: string; answer: string }> = [];
          for (const section in botMsg.sectionResponses) {
            answers.push({
              section,
              answer: botMsg.sectionResponses[section]
            });
          }
          chatHistory.push({
            question: userMsg.text,
            answers
          });
        }
      }
    }
    return chatHistory;
  };

  const AskQuestionSectionsStream = async (
    text: string,
    conversation: Conversation
  ): Promise<AskQuestionSectionsStreamResponseChunk> => {
    const chatHistory = buildChatHistory(conversation.messages);
    const request: AskQuestionSectionsStreamRequestDTO = {
      question: text,
      index: conversation.ragIndex,
      chatHistory,
      conversationId: conversation.id
    };
    const response = await legalAssistantService.askQuestionSectionsStream(
      request,
      (chunk) => onChunk(chunk, conversation)
    );

    return response;
  };

  const sendMessage = async (
    assistant: AssistantType,
    text: string,
    conversation: Conversation
  ) => {
    const storeActions = store.createAssistantActions();

    resetReactiveState();

    try {
      setHeaderData(assistant, { loading: true });

      storeActions.clearSelectedText();

      let response;

      const streaming = useStreaming(computed(() => conversation.ragIndex));
      if (streaming.value) {
        response = await AskQuestionSectionsStream(text, conversation);
      } else {
        response = await askQuestion(
          text,
          conversation,
          AssistantType.LegalAssistant
        );
      }

      return {
        responseMessage: new LegalAssistantMessage(
          response
            ? typeof response === 'string'
              ? response
              : response.answer
            : '',
          true,
          response && typeof response === 'object' && 'sources' in response
            ? response.sources
            : []
        )
      };
    } catch (error) {
      setHeaderData(assistant, { loading: false });
      throw error;
    }
  };

  const legalAssistantProps = {
    assistantType: AssistantType.LegalAssistant,
    onSend: async (text: string, conversation: Conversation) =>
      await sendMessage(AssistantType.LegalAssistant, text, conversation),
    allowDocumentTextSelection: false,
    botMessageFooter: (message: LegalAssistantMessage) => {
      if (process.env.VUE_APP_COUNTRY === CountryCodes.SPAIN) return null;
      return h(MessageSources, {
        sources: message.sources ?? [],
        assistantType: AssistantType.LegalAssistant,
        renderId: uuidv4()
      });
    },
    streaming: false
  };

  const setChunkWithoutAnswer = () => {
    const section = getCurrentSection();
    const chunk = sectionChunks.value[section];
    const found = chunk
      ? INVALID_ANSWERS.find((invalid) => invalid === chunk.trim())
      : undefined;
    legalAssistantRef.value?.handleChunk(
      found || INVALID_ANSWERS[FIRST_SECTION]
    );
  };

  watch(
    () => store.legalAssistantSelectedText,
    () => {
      if (store.legalAssistantSelectedText) {
        resetReactiveState();
        if (activeTabIndex.value === 1) activeTabIndex.value = 0;
        legalAssistantRef.value?.handleSend(store.legalAssistantSelectedText);
      }
    }
  );

  watch([sectionChunks, sectionSources, currentSectionIndex], () => {
    const section = getCurrentSection();
    if (!(section in sectionChunks.value)) return;
    const chunk = sectionChunks.value[section];
    const source = sectionSources.value[section];

    if (isValidAnswer(chunk)) {
      legalAssistantRef.value?.handleChunk(chunk, section);
      legalAssistantRef.value?.handleSources(source);
      return;
    }

    if (currentSectionIndex.value < SECTION_PRIORITY.length - 1) {
      nextSection();
    } else {
      setChunkWithoutAnswer();
    }
  });

  onMounted(() => {
    if (store.legalAssistantSelectedText) {
      resetReactiveState();
      legalAssistantRef.value?.handleSend(store.legalAssistantSelectedText);
      store.createAssistantActions().clearSelectedText();
    }
  });

  return { legalAssistantRef, legalAssistantProps };
}
